import { useMemo } from 'react';
import I18n from 'I18n';
import { gate } from 'hub-http/gates';
import { useGetHasScope, useGetIsUngated, useHasScope } from 'automation-ui-core/hooks/authHooks';
import { CUSTOM_EVENTS_GROUP, INTEGRATOR_EVENTS_GROUP } from 'automation-ui-enrollment/constants/TriggerGroups';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { PERMISSIONS_CUSTOM_EVENTS } from '../constants/Gates';
import { BEHAVIORAL_EVENTS_READ, CUSTOM_EVENTS_VIEW } from 'automation-ui-enrollment/constants/Scopes';
import useGetEventPropertyFieldsMap, { getViewOnlyProperties } from 'automation-ui-enrollment/hooks/useGetEventPropertyFieldsMap';
import useHasListsToolAccess from 'automation-ui-enrollment/hooks/useHasListsToolAccess';
import { GET_BEHAVIORAL_EVENTS, GET_INTEGRATOR_EVENTS } from 'automation-ui-enrollment/queries/EventQueries';
import { GET_EVENT_TRIGGERS_METADATA } from 'automation-ui-enrollment/queries/FrameworkBuilderQueries';
import { GET_WEBHOOK_DEFINITIONS } from 'automation-ui-enrollment/queries/WebhookQueries';
import { InboundDbListSegStrategy } from 'customer-data-filters-ui/public/strategies';
import { useQuery } from 'data-fetching-client';
import { OrderedMap } from 'immutable';
import { getListMembershipEventName } from 'automation-ui-enrollment/utils/listMembershipUtils';
import { TriggerCategories } from 'automation-ui-enrollment/constants/TriggerCategories';
import sortByText from 'automation-ui-enrollment/utils/sortByText';
import { convertWebhookToEventTriggerOption } from 'automation-ui-enrollment/utils/webhookUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const useCustomBehavioralEventFilterFamilies = ({
  objectTypeId
}) => {
  const useUpdatedCEScopes = useIsUngated(PERMISSIONS_CUSTOM_EVENTS);
  // CBEs are defined per-portal, so add a mocked type and merge it in with the rest of the types in the seletion step
  const {
    data: behavioralEventsData,
    loading: behavioralEventsLoading,
    error: behavioralEventsError
  } = useQuery(GET_BEHAVIORAL_EVENTS, {
    skip: !useHasScope(useUpdatedCEScopes ? CUSTOM_EVENTS_VIEW : BEHAVIORAL_EVENTS_READ),
    variables: {
      objectTypeId
    }
  });
  if (useHasScope(useUpdatedCEScopes ? CUSTOM_EVENTS_VIEW : BEHAVIORAL_EVENTS_READ) && behavioralEventsData && !behavioralEventsError) {
    return {
      loading: behavioralEventsLoading,
      options: behavioralEventsData.behavioralEvents.results.map(behavioralEvent => {
        var _behavioralEvent$desc;
        return {
          text: behavioralEvent.labels.singular,
          entityName: behavioralEvent.labels.singular,
          groupHeading: behavioralEvent.labels.singular,
          value: behavioralEvent.objectTypeId,
          supportedObjectTypeIds: behavioralEvent.primaryObjectId ? [behavioralEvent.primaryObjectId] : [],
          fullyQualifiedName: behavioralEvent.fullyQualifiedName,
          categoryId: 'advanced',
          groupId: CUSTOM_EVENTS_GROUP,
          groupLabel: I18n.text('automation-ui-enrollment.redesign.events.groups.custom-behavioral-event'),
          description: (_behavioralEvent$desc = behavioralEvent.description) !== null && _behavioralEvent$desc !== void 0 ? _behavioralEvent$desc : undefined
        };
      })
    };
  }
  return {
    loading: behavioralEventsLoading,
    options: []
  };
};
const useIntegratorEventFilterFamilies = ({
  objectTypeId
}) => {
  const {
    data: integratorEventsData,
    loading: integratorEventsLoading,
    error: integratorEventsError
  } = useQuery(GET_INTEGRATOR_EVENTS);
  if (integratorEventsData && integratorEventsData.integratorEvents.length && !integratorEventsError) {
    return {
      loading: integratorEventsLoading,
      options: integratorEventsData.integratorEvents.filter(event => !objectTypeId || !event.objectTypeId || event.objectTypeId === objectTypeId).map(integratorEvent => {
        return {
          text: integratorEvent.name,
          entityName: integratorEvent.name,
          groupHeading: integratorEvent.name,
          value: integratorEvent.foreignEventTypeId,
          supportedObjectTypeIds: [integratorEvent.objectTypeId],
          fullyQualifiedName: integratorEvent.foreignEventTypeId,
          categoryId: 'advanced',
          groupId: INTEGRATOR_EVENTS_GROUP,
          groupLabel: I18n.text('automation-ui-enrollment.redesign.events.groups.integrator-event')
        };
      })
    };
  }
  return {
    loading: integratorEventsLoading,
    options: []
  };
};
const useWebhookTriggersFilterFamilies = ({
  objectTypeId
}) => {
  const {
    data: webhookEventData,
    loading: webhookEventsLoading
  } = useQuery(GET_WEBHOOK_DEFINITIONS, {
    variables: {
      objectTypeId
    }
  });
  if (webhookEventData) {
    const webhooks = webhookEventData.webhookDefinitions.results;
    return {
      loading: false,
      options: webhooks.map(convertWebhookToEventTriggerOption)
    };
  }
  return {
    loading: webhookEventsLoading,
    options: []
  };
};
const useListMembershipFilterFamilies = ({
  objectTypeId
}) => {
  // Replacement for `requiredObjectTypeIds`, these are the only types that can use lists
  if (useHasListsToolAccess(objectTypeId !== null && objectTypeId !== void 0 ? objectTypeId : '') || !objectTypeId) {
    const text = I18n.text('automation-ui-enrollment.redesign.events.types.LIST_MEMBERSHIP_CHANGED.text');
    return {
      loading: false,
      options: [{
        /* `value` replaces the standard event UI with the list selection flow
        List membership events are powered by lists behind-the-scenes, so we have no event to pull types/properties from
        and mock event UI doesn't fit our restrictions (must only contain `listId` and if the list is added/removed) */
        value: getListMembershipEventName(objectTypeId),
        text,
        entityName: text,
        // list membership isn't an actual event so it needs special formatting to match the group heading UI
        groupHeading: /*#__PURE__*/_jsx("b", {
          children: I18n.text('automation-ui-enrollment.redesign.events.types.LIST_MEMBERSHIP_CHANGED.groupHeading')
        }),
        supportedObjectTypeIds: [],
        fullyQualifiedName: getListMembershipEventName(objectTypeId),
        categoryId: 'data',
        description: I18n.text('automation-ui-enrollment.redesign.events.types.LIST_MEMBERSHIP_CHANGED.description')
      }]
    };
  }
  return {
    loading: false,
    options: []
  };
};
const useUnifiedEventFilterFamilies = ({
  objectTypeId
}) => {
  const hasScope = useGetHasScope();
  const isUngated = useGetIsUngated();
  const {
    data: eventTriggersMetadataData,
    loading
  } = useQuery(GET_EVENT_TRIGGERS_METADATA);
  const allEnrollmentTypes = eventTriggersMetadataData === null || eventTriggersMetadataData === void 0 ? void 0 : eventTriggersMetadataData.eventTriggersMetadata;
  const filterFamilies = useMemo(() => {
    if (!allEnrollmentTypes) {
      return [];
    }
    const options = [];
    Object.entries(allEnrollmentTypes).forEach(([eventFQN, schema]) => {
      var _schema$blockedObject;
      const applicableToObjectType = !objectTypeId || (schema.requiredObjectTypeIds.length === 0 || schema.requiredObjectTypeIds.includes(objectTypeId)) && !((_schema$blockedObject = schema.blockedObjectTypeIds) !== null && _schema$blockedObject !== void 0 && _schema$blockedObject.includes(objectTypeId));
      const hasAccess = schema.requiredScopes.every(scope => hasScope(scope)) && schema.requiredGates.every(requiredGate =>
      // v2 and v3 gates already do not have name collisions and we must query v3 gates using their prefix
      // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      isUngated(requiredGate) || isUngated(gate(requiredGate))) && applicableToObjectType;
      if (hasAccess) {
        var _schema$triggerCatego;
        options.push({
          value: schema.objectTypeId,
          // for filter viewer
          entityName: schema.singularForm,
          // for "has completed"/"has not completed" select, which shouldn't be visible
          groupHeading: schema.singularForm,
          // for event select
          text: schema.singularForm,
          supportedObjectTypeIds: schema.requiredObjectTypeIds,
          blockedObjectTypeIds: schema.blockedObjectTypeIds,
          fullyQualifiedName: eventFQN,
          categoryId: (_schema$triggerCatego = schema.triggerCategoryId) !== null && _schema$triggerCatego !== void 0 ? _schema$triggerCatego : 'advanced',
          groupId: schema.triggerGroupId,
          groupLabel: schema.triggerGroupLabel,
          description: schema.triggerDescription
        });
      }
    });
    return options;
  }, [allEnrollmentTypes, hasScope, isUngated, objectTypeId]);
  return {
    loading,
    options: filterFamilies
  };
};
export const useGetInjectedFilterFamilies = ({
  objectTypeId,
  strategyOptions: {
    showListMembershipFilterFamily
  }
}) => {
  let injectedFilterFamiliesLoading = false;
  const filterFamilyGetterHooks = [useUnifiedEventFilterFamilies, useCustomBehavioralEventFilterFamilies, useWebhookTriggersFilterFamilies, useIntegratorEventFilterFamilies];
  if (showListMembershipFilterFamily) {
    filterFamilyGetterHooks.push(useListMembershipFilterFamilies);
  }
  let options = [];
  filterFamilyGetterHooks.forEach(getter => {
    const {
      loading,
      options: injectedOptions
    } = getter({
      objectTypeId
    });
    injectedFilterFamiliesLoading = injectedFilterFamiliesLoading || loading;
    options = options.concat(injectedOptions);
  });

  // todo integrator events filter family

  return {
    injectedFilterFamilies: options,
    injectedFilterFamiliesLoading
  };
};
const useEventEnrollmentStrategy = ({
  objectTypeId,
  value,
  strategyOptions
}) => {
  const {
    eventPropertyFieldsMap,
    initialized
  } = useGetEventPropertyFieldsMap(value);
  const {
    injectedFilterFamilies,
    injectedFilterFamiliesLoading
  } = useGetInjectedFilterFamilies({
    objectTypeId,
    strategyOptions
  });
  const getFilterFamilies = (filterFamily, isAssociation) => {
    if (injectedFilterFamiliesLoading || isAssociation) {
      return [];
    }
    const groups = new Map();
    injectedFilterFamilies.forEach(option => {
      const groupId = option.groupId || option.categoryId;
      const group = groups.get(groupId);
      if (group) {
        group.options.push(option);
      } else {
        groups.set(groupId, {
          text: option.groupLabel || I18n.text(TriggerCategories[option.categoryId].fallbackTitleKey),
          options: [option],
          sortOrder: option.categoryId === 'advanced' ? 1 : 0
        });
      }
    });
    return sortByText(Array.from(groups.values()).map(({
      text,
      options,
      sortOrder
    }) => ({
      text,
      options: sortByText(options),
      sortOrder
    })));
  };
  const getFields = (filterFamily, originalFields) => {
    if (!eventPropertyFieldsMap[filterFamily]) {
      return originalFields;
    }
    const eventPropertiesFields = Object.keys(eventPropertyFieldsMap[filterFamily]).reduce((acc, key) => {
      const field = eventPropertyFieldsMap[filterFamily][key];
      return field ? acc.set(key, field) : acc;
    }, OrderedMap());
    return eventPropertiesFields;
  };

  // according to the docs, getFields and getField must return the same value by id
  const getField = (filterFamily, id, originalField) => {
    // this works bc getField is only used to display the left-side filter information, and not the list of available properties
    // long-term we should remove this function & the blockers causing it to exist
    const viewOnlyProperties = getViewOnlyProperties();
    if (eventPropertyFieldsMap[filterFamily] && eventPropertyFieldsMap[filterFamily][id]) {
      return eventPropertyFieldsMap[filterFamily][id];
    }
    if (viewOnlyProperties[id]) {
      return viewOnlyProperties[id];
    }
    return originalField;
  };
  const strategy = InboundDbListSegStrategy.configure({
    // lets user add events without needing to add additonal filters
    // ex: "has completed event" instead of "has completed event where property is known"
    isNotCompletedUnifiedEventFiltersEnabled: false,
    conditionsPerORGroupLimit: 1,
    // keeps events from being AND-ed together
    getFilterFamilies,
    getFields,
    getField,
    isSameObjectAssociationFilteringEnabled: true
  });
  return {
    strategy,
    initialized
  };
};
export default useEventEnrollmentStrategy;